import {useEffect, useState} from 'react';
import {Alert, Button, Form, Input} from '@vector';
import {ArrowRightBoldIcon} from '@solar-icons/react';
import {sendContactMessage} from './homeService';
import {getRecaptchaToken} from '../portal/recaptcha';
import Layout from './components/Layout';
import brandLayoutProps from './components/brandLayoutProps';
import VideoSection from './components/VideoSection';
import styles from './EdjinHome.module.css';

const FIELD_MAX = 200;
const MESSAGE_MAX = 4000;

/**
 * Edjin's homepage (content/edjin.js): a marketing page for the platform, in the HOTmaths homepage's chrome and type
 * but Edjin's colours. No sign-in, trial or pricing; the one action is the contact form.
 */
export default function EdjinHome({content}) {
    useEffect(() => {
        document.title = `${content.productName} - ${content.intro.heading}`;
    }, [content]);

    return (
        <Layout {...brandLayoutProps(content)} showSignIn={false} showJoin={false}
                headerAction={<Button surface="accent" href="#ch-edjin-contact">Contact us</Button>}>
            <Hero content={content.intro}/>
            <Features content={content.features}/>
            <Products content={content.products}/>
            <Awards content={content.awards}/>
            <VideoSection content={content.video}/>
            <Contact content={content.contact}/>
        </Layout>
    );
}

function Hero({content}) {
    return (
        <section className={styles.hero} style={{'--edjin-hero-image': `url("${content.backgroundImage}")`}}>
            <div className={styles.heroInner}>
                <h1 className={styles.heroHeading}>{content.heading}</h1>
                <p className={styles.heroStandfirst}>{content.standfirst}</p>
                <div className={styles.heroActions}>
                    <Button surface="accent" size="large" href={content.primaryAction.href}
                            icon={<ArrowRightBoldIcon size={20}/>} iconPosition="end">
                        {content.primaryAction.label}
                    </Button>
                    <Button size="large" href={content.secondaryAction.href}>
                        {content.secondaryAction.label}
                    </Button>
                </div>
            </div>
        </section>
    );
}

function Features({content}) {
    return (
        <section id="ch-edjin-features" className={styles.section} aria-labelledby="edjin-features">
            <div className={styles.inner}>
                <h2 id="edjin-features" className={styles.heading}>{content.heading}</h2>
                <p className={styles.standfirst}>{content.standfirst}</p>
                <ul className={styles.cards}>
                    {content.items.map((item) => (
                        <li key={item.title} className={styles.card}>
                            <img className={styles.featureImage} src={item.image} alt="" loading="lazy"/>
                            <h3 className={styles.cardTitle}>{item.title}</h3>
                            <p className={styles.cardBody}>{item.body}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function Products({content}) {
    return (
        <section id="ch-edjin-products" className={`${styles.section} ${styles.tinted}`}
                 aria-labelledby="edjin-products">
            <div className={styles.inner}>
                <h2 id="edjin-products" className={styles.heading}>{content.heading}</h2>
                <ul className={styles.products}>
                    {content.items.map((item) => {
                        const body = (
                            <>
                                <img className={styles.productImage} src={item.image} alt="" loading="lazy"/>
                                <span className={styles.productName}>{item.name}</span>
                            </>
                        );
                        return (
                            <li key={item.name}>
                                {item.href ? (
                                    <a className={styles.product} href={item.href} target="_blank"
                                       rel="noopener noreferrer">{body}</a>
                                ) : (
                                    <div className={styles.product}>{body}</div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}

function Awards({content}) {
    return (
        <section id="ch-edjin-awards" className={styles.section} aria-labelledby="edjin-awards">
            <div className={styles.inner}>
                <h2 id="edjin-awards" className={styles.heading}>{content.heading}</h2>
                <ul className={styles.cards}>
                    {content.items.map((item) => (
                        <li key={item.title} className={styles.card}>
                            <img className={styles.awardImage} src={item.image} alt="" loading="lazy"/>
                            <h3 className={styles.cardTitle}>{item.title}</h3>
                            <p className={styles.cardBody}>{item.body}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

/** Posts to /web/ui/home/public/contact, which checks reCAPTCHA, rate limits and emails the brand's inbox. */
function Contact({content}) {
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (values) => {
        setBusy(true);
        setResult(null);
        try {
            const recaptchaToken = await getRecaptchaToken('contact');
            const outcome = await sendContactMessage({...values, recaptchaToken});
            setResult(outcome);
            if (outcome.sent) {
                form.resetFields();
            }
        } catch (e) {
            setResult({sent: false, message: 'We could not send your message. Please try again in a moment.'});
        } finally {
            setBusy(false);
        }
    };

    const required = (label) => [{required: true, whitespace: true, message: `Please enter ${label}.`}];

    return (
        <section id="ch-edjin-contact" className={styles.section} aria-labelledby="edjin-contact">
            <div className={`${styles.inner} ${styles.contact}`}>
                <div className={styles.contactIntro}>
                    <h2 id="edjin-contact" className={styles.heading}>{content.heading}</h2>
                    <p className={styles.standfirst}>{content.body}</p>
                </div>

                <div className={styles.contactCard}>
                    <Form form={form} layout="vertical" requiredMark onFinish={submit} disabled={busy}>
                        <Form.Item label="Name" name="name" rules={required('your name')}>
                            <Input autoComplete="name" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Email" name="email"
                                   rules={[...required('your email address'),
                                       {type: 'email', message: 'Please enter a valid email address.'}]}>
                            <Input autoComplete="email" inputMode="email" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Organisation" name="organisation">
                            <Input autoComplete="organization" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Message" name="message" rules={required('a message')}>
                            <Input.TextArea rows={5} maxLength={MESSAGE_MAX} showCount/>
                        </Form.Item>

                        <div role="status" aria-live="polite">
                            {result && (
                                <Alert className={styles.alert} type={result.sent ? 'success' : 'error'}
                                       title={result.message} showIcon/>
                            )}
                        </div>

                        <Button surface="accent" size="large" htmlType="submit" loading={busy}>
                            Send message
                        </Button>
                    </Form>
                </div>
            </div>
        </section>
    );
}
